import React, { useState } from "react";
import Image from "next/image";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ContentLayoutFooterNoChartreuse from "../components/Layout/ContentLayoutFooterNoChartreuse";

interface FAQItemProps {
  question: string;
  answer: React.ReactNode;
  category: string;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <li className="border-b border-gray-300 py-4">
      <h2 className="flex justify-between items-center text-xl">
        {question}
        {isExpanded ? (
          <KeyboardArrowDownIcon
            className="cursor-pointer text-gray-400 ml-2"
            onClick={toggleExpanded}
          />
        ) : (
          <KeyboardArrowUpIcon
            className="cursor-pointer text-gray-400 ml-2"
            onClick={toggleExpanded}
          />
        )}
      </h2>
      {isExpanded && <p className="text-gray-500 text-sm mt-2">{answer}</p>}
    </li>
  );
};

const FAQ = () => {
  const faqItems: FAQItemProps[] = [
    {
      question: "Why should I purchase wall art from Raraity?",
      answer:
        "Raraity is an AI atelier that translates your creative vision into reality. We use machine learning and artificial intelligence to create custom, high-quality wall art that is unique to your personal space and taste. Our platform offers instant design and quality results, making the process easy and hassle-free.",
      category: "General",
    },
    {
      question: "How is Raraity's wall art created?",
      answer:
        "Raraity's wall art is created using machine learning and artificial intelligence to deliver custom, high-quality designs that are unique to your personal style. Our platform is designed to translate your creative vision into reality, allowing you to create wall art that is truly one-of-a-kind.",
      category: "General",
    },
    {
      question: "What materials are used to create Raraity's wall art?",
      answer:
        "Raraity uses high-quality materials to create our wall art, including premium archival inks and museum-quality paper. Our team works closely with collectors to ensure that the materials used are of the highest quality, and that each piece of wall art is crafted with care and attention to detail.",
      category: "Product Details",
    },
    {
      question: "Does Raraity offer custom framing options for its wall art?",
      answer:
        "Yes, we offer custom framing options for our wall art. Every piece comes printed on archival-grade paper: protected by custom maple frames, Museum Acrylic®, and handcrafted by our team of professionals. Our collectors choose frame color, size, and mounting method. Our solid wood frames are designed to beautifully complement and protect your artwork.",
      category: "Product Details",
    },
    {
      question: "Can Raraity's wall art be used for commercial purposes?",
      answer:
        "Yes, Raraity's wall art can be used for commercial purposes. Please contact us at hi@raraity.com to discuss your specific needs and to arrange for any necessary licensing agreements. We offer competitive pricing and flexible licensing options for our customers.",
      category: "General",
    },
    {
      question: "Can I combine multiple gift cards on the same purchase?",
      answer:
        "We are currently not able to process multiple gift cards on the same transaction. If you would like to multiple gift cards or credits, please reach out to customer service at hi@raraity.com so that we can help you combine your gift cards.",
      category: "General",
    },
    {
      question: "What are my mounting options?",
      answer: (
        <ul className="list-disc pl-4">
          <li>
            <strong>Full Bleed</strong> | Extends edge-to-edge, showcasing your
            artwork without borders or matting.
          </li>
          <br />
          <li>
            <strong>Mat</strong> | Classic framing choice with a single-layer
            mat border, providing a clean and elegant separation between your
            artwork and the frame.
          </li>
          <br />
          <li>
            <strong>Island Mat</strong> | Distinct border that visually
            separates your artwork from the frame, using a secondary mat layer
            to create a surrounding island.
          </li>
          <br />
          <li>
            <strong>Raised Float</strong> | Sophisticated mounting technique
            that elevates your artwork, giving it a floating appearance within
            the frame for added depth and dimension.
          </li>
        </ul>
      ),
      category: "Product Details",
    },
    {
      question: "What are my framing options?",
      answer:
        'Each frame is designed for museum-grade artwork to ensure that your piece is ready for installation and exhibition. The custom, hand-painted maple frame has a 3/4" face and 1.25" depth. Our craftsmen finish to your preference of white, black, natural or mahogany.',
      category: "Product Details",
    },
    {
      question: "What hardware comes with my artwork?",
      answer: (
        <div>
          <ul className="list-disc pl-4">
            <li>
              <strong>Cleat System</strong> | A cleat system is a secure and
              versatile method for hanging artwork. It consists of two
              interlocking pieces: a wall-mounted cleat and a corresponding
              cleat attached to the back of the frame. Cleat systems offer
              excellent stability and security, ensuring that your artwork
              remains level and firmly in place.
            </li>
            <br />
            <li>
              <strong>Wire Hanging</strong> | We attach a sturdy wire across the
              back of the frame for you to suspend your artwork from and we
              provide a hook for your wall.
            </li>
          </ul>
          <br />
          <h2>
            Ultimately, the choice between a cleat system and wire hanging
            depends on your personal preferences, the weight and size of the
            artwork, and the level of stability you desire. If you value a
            secure and precise installation, with minimal visible hardware, a
            cleat system might be the better option. However, if you prefer
            simplicity, adjustability, and are comfortable with wire hanging&apos;s
            characteristics, that might be the right choice for you.
          </h2>
        </div>
      ),
      category: "Product Details",
    },
    {
      question: "What are the artwork's dimensions?",
      answer: (
        <div>
          <h2>
            All artwork dimensions are a function of the overall size and
            mounting option chosen. Below are example dimensions for a 10x10&quot;
            piece:
          </h2>
          <br />
          <Image
            className="w-112"
            src="https://res.cloudinary.com/dj2nr2jlm/image/upload/c_scale,w_1500/v1683848335/frameddimensions_z8coly.png"
            alt=""
          />
        </div>
      ),
      category: "Product Details",
    },
    {
      question: "What is Museum Acrylic®?",
      answer:
        "Museum Acrylic®, the preferred choice of the world's top museums, is renowned for its unmatched quality. By virtually eliminating all reflections, it offers a breathtaking, crystal-clear viewing experience that allows every delicate detail of the artwork to shine without any distracting glare. With its advanced technology, scientific formulation, and exceptional features including superior transparency, 99% UV protection, and unparalleled durability, this premium acrylic ensures your fine art is flawlessly showcased, providing permanent protection and preserving its clarity for years to come.",
      category: "Product Details",
    },
  ];

  const groupedFaqItems: Record<string, FAQItemProps[]> = {};
  faqItems.forEach((item) => {
    if (!groupedFaqItems[item.category]) {
      groupedFaqItems[item.category] = [];
    }
    groupedFaqItems[item.category].push(item);
  });

  return (
    <ContentLayoutFooterNoChartreuse title="FAQ">
      <div className="container m-auto p-4">
        <h1 className="text-xl mb-8">Frequently Asked Questions</h1>
        {Object.entries(groupedFaqItems).map(([category, items]) => (
          <div key={category} className="mb-8">
            <h1 className="text-lg mb-4">{category}</h1>
            <ul>
              {items.map((item) => (
                <FAQItem
                  key={item.question}
                  question={item.question}
                  answer={item.answer}
                  category={item.category}
                />
              ))}
            </ul>
          </div>
        ))}
      </div>
    </ContentLayoutFooterNoChartreuse>
  );
};

export default FAQ;

export async function getStaticProps() {
  return {
    props: { page: "FAQ" },
  };
}
